// Journal posts: videos in the photo grid autoplay muted (browsers only allow silent autoplay).
// Each has a SOUND ON / SOUND OFF button (sections/blog-article.liquid): only one video plays
// sound at a time, and a video that scrolls out of view goes quiet again.
const buttons = Array.from(document.querySelectorAll('.article__video-sound')) as HTMLButtonElement[];

const videoOf = (button: HTMLButtonElement) =>
  button.parentElement?.querySelector('video') as HTMLVideoElement | null;

const setSound = (button: HTMLButtonElement, on: boolean) => {
  const video = videoOf(button);
  if (!video) return;

  video.muted = !on;
  if (on) video.play().catch(() => {});
  button.textContent = on ? 'SOUND OFF' : 'SOUND ON';
  button.setAttribute('aria-pressed', String(on));
};

buttons.forEach((button) => {
  button.addEventListener('click', () => {
    const video = videoOf(button);
    if (!video) return;

    const turnOn = video.muted;
    buttons.forEach((other) => {
      if (other !== button) setSound(other, false);
    });
    setSound(button, turnOn);
  });
});

// The arrow fills the screen with the video; turned back in, it comes out again.
// The whole frame goes full screen, so our own buttons come along; an iPhone only
// allows its own player, so there the video is handed over to it and its Done button.
const fullButtons = Array.from(document.querySelectorAll('.article__video-full')) as HTMLButtonElement[];

type FullscreenFrame = HTMLElement & { webkitRequestFullscreen?: () => void };
type FullscreenVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

fullButtons.forEach((button) => {
  button.addEventListener('click', () => {
    const frame = button.parentElement as FullscreenFrame | null;
    const video = frame?.querySelector('video') as FullscreenVideo | null;
    if (!frame || !video) return;

    if (document.fullscreenElement === frame) {
      document.exitFullscreen().catch(() => {});
      return;
    }

    if (frame.requestFullscreen) frame.requestFullscreen().catch(() => video.webkitEnterFullscreen?.());
    else if (frame.webkitRequestFullscreen) frame.webkitRequestFullscreen();
    else video.webkitEnterFullscreen?.();
  });
});

// Full screen: the browser hides the mouse once it stops moving, so the arrow and
// SOUND ON go quiet with it and all come back on the next movement.
if (fullButtons.length) {
  const idleAfter = 2500;
  let idleTimer: number | undefined;
  let frame: HTMLElement | null = null;

  const wake = () => {
    if (!frame) return;

    frame.classList.add('has-pointer');
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => frame?.classList.remove('has-pointer'), idleAfter);
  };

  document.addEventListener('fullscreenchange', () => {
    window.clearTimeout(idleTimer);
    frame?.classList.remove('has-pointer');
    document.removeEventListener('mousemove', wake);

    frame = document.fullscreenElement as HTMLElement | null;
    if (!frame) return;

    document.addEventListener('mousemove', wake);
    wake();
  });
}

// hide the button on videos without sound: once a video has played a moment, Chrome and
// Safari report how much audio they decoded (0 = silent); Firefox reports it directly
type AudioInfo = { webkitAudioDecodedByteCount?: number; mozHasAudio?: boolean };

buttons.forEach((button) => {
  const video = videoOf(button);
  if (!video) return;

  const check = () => {
    if (video.currentTime < 1) return;
    video.removeEventListener('timeupdate', check);
    const info = video as HTMLVideoElement & AudioInfo;
    const silent = info.mozHasAudio === false || info.webkitAudioDecodedByteCount === 0;
    if (silent) button.hidden = true;
  };
  video.addEventListener('timeupdate', check);
});

// sound off when a playing video leaves the screen
if (buttons.length && 'IntersectionObserver' in window) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) return;
      const button = buttons.find((b) => videoOf(b) === entry.target);
      if (button && !(entry.target as HTMLVideoElement).muted) setSound(button, false);
    });
  });

  buttons.forEach((button) => {
    const video = videoOf(button);
    if (video) observer.observe(video);
  });
}
